/**
 * Belege und Kontoauszug nebeneinander.
 *
 * Die Einzelansicht beantwortet „was steckt hinter dieser Buchung?". Diese
 * hier beantwortet eine andere Frage, die man am Bildschirm sonst nur mühsam
 * stellt: Wie hängt das Ganze zusammen? Links die Belege, rechts die
 * Buchungen, und beim Überfahren einer Zeile leuchten ihre Gegenstücke auf.
 *
 * Drei Entscheidungen stecken darin:
 *
 * Die Hervorhebung geht in beide Richtungen. Ein Beleg zeigt seine Zahlungen,
 * eine Zahlung ihre Belege — bei einer Sammelüberweisung sind das mehrere auf
 * einmal, und genau dieser Fall ist in einer Liste sonst unsichtbar.
 *
 * Fokus wirkt wie Überfahren. Eine Hervorhebung, die nur auf die Maus hört,
 * schließt jeden aus, der mit der Tastatur arbeitet — und ein Beirat, der zwei
 * Stunden lang Belege durchgeht, arbeitet irgendwann mit der Tastatur.
 *
 * Ein Klick hält die Hervorhebung fest. Beim Vergleichen zweier weit
 * auseinanderliegender Zeilen muss man scrollen, und beim Scrollen verliert
 * man das Überfahren. Festgehalten bleibt die Verbindung sichtbar, bis man
 * woanders hinklickt.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { datum, datumKurz, eur } from '../lib/api';
import { Haken } from '../komponenten/Haken';
import type {
  Banktransaktion,
  Beleg,
  PruefStatus,
  Zahlungsbefund,
  Zuordnung,
} from '../lib/typen';

export interface GegenueberstellungProps {
  belege: Beleg[];
  transaktionen: Banktransaktion[];
  zahlungen: Record<string, Zahlungsbefund>;
  /**
   * Alle Zuordnungen, auch die vorgeschlagenen.
   *
   * Der Zahlungsbefund lässt Vorschläge bewusst außen vor — ein Vorschlag
   * ist eine Vermutung des Rechners, kein Prüfergebnis. Hier ist es anders
   * herum: diese Ansicht dient dem Hinsehen, und gerade die vermutete
   * Aufteilung einer Sammelüberweisung ist das, was ein Mensch bestätigen
   * oder verwerfen soll. Sie wird deshalb gezeigt, nur erkennbar anders.
   */
  zuordnungen: Zuordnung[];
  ohneBeleg: Set<string>;
  /** Prüfstatus je Kontoauszugsposition. */
  transaktionStatus: Record<string, PruefStatus>;
  onBeleg: (belegId: string) => void;
  onRueckfrage: (transaktionId: string) => void;
  /** Eine Position als geprüft bestätigen oder die Bestätigung lösen. */
  onTransaktionStatus: (transaktionId: string, status: PruefStatus) => void;
  /** Beleg und Position von Hand verknüpfen. */
  onVerknuepfen: (transaktionId: string, belegId: string) => void;
  /** Eine Verknüpfung wieder lösen oder einen Vorschlag bestätigen. */
  onZuordnung: (zuordnungId: string, was: 'bestaetigen' | 'loesen') => void;
}

/** Was gerade betrachtet wird — durch Überfahren, Fokus oder Klick. */
type Auswahl = { art: 'beleg' | 'buchung'; id: string } | null;

export function Gegenueberstellung({
  belege,
  transaktionen,
  zahlungen,
  zuordnungen,
  ohneBeleg,
  transaktionStatus,
  onBeleg,
  onRueckfrage,
  onTransaktionStatus,
  onVerknuepfen,
  onZuordnung,
}: GegenueberstellungProps) {
  const [schwebend, setSchwebend] = useState<Auswahl>(null);
  const [fixiert, setFixiert] = useState<Auswahl>(null);
  const [nurVerknuepfte, setNurVerknuepfte] = useState(false);
  /** Auf die festgehaltene Verbindung einengen — beide Spalten. */
  const [nurDiese, setNurDiese] = useState(false);
  const [mitVorschlaegen, setMitVorschlaegen] = useState(true);

  const belegSpalte = useRef<HTMLDivElement>(null);
  const buchungSpalte = useRef<HTMLDivElement>(null);

  // Festgehaltenes schlägt Überfahren: sonst reißt die Verbindung ab, sobald
  // der Zeiger auf dem Weg zur anderen Spalte eine fremde Zeile streift.
  const aktiv = fixiert ?? schwebend;

  const transaktionNach = useMemo(
    () => new Map(transaktionen.map((t) => [t.id, t])),
    [transaktionen],
  );

  /**
   * Die Verknüpfungen in beide Richtungen, bestätigte und vorgeschlagene
   * getrennt — damit die Oberfläche beides zeigen und unterscheiden kann.
   */
  const { buchungenJeBeleg, belegeJeBuchung, vorschlagBelege, vorschlagBuchungen } = useMemo(() => {
    const jeBeleg = new Map<string, string[]>();
    const jeBuchung = new Map<string, string[]>();
    const vB = new Set<string>();
    const vT = new Set<string>();

    for (const z of zuordnungen) {
      const bestaetigt = z.status === 'zugeordnet';
      const vorschlag = z.status === 'vorschlag';
      if (!bestaetigt && !(vorschlag && mitVorschlaegen)) continue;

      jeBeleg.set(z.belegId, [...(jeBeleg.get(z.belegId) ?? []), z.transaktionId]);
      jeBuchung.set(z.transaktionId, [...(jeBuchung.get(z.transaktionId) ?? []), z.belegId]);
      if (vorschlag) {
        vB.add(z.belegId);
        vT.add(z.transaktionId);
      }
    }
    return {
      buchungenJeBeleg: jeBeleg,
      belegeJeBuchung: jeBuchung,
      vorschlagBelege: vB,
      vorschlagBuchungen: vT,
    };
  }, [zuordnungen, mitVorschlaegen]);

  const vorschlaegeVorhanden = useMemo(
    () => zuordnungen.some((z) => z.status === 'vorschlag'),
    [zuordnungen],
  );

  /**
   * Die beiden Mengen, die gerade leuchten.
   *
   * Die angefasste Zeile selbst gehört dazu — sonst leuchtet das Gegenstück
   * auf, während die Quelle blass bleibt, und man sucht, wo man eigentlich
   * gerade steht.
   */
  const { belegeHell, buchungenHell } = useMemo(() => {
    const b = new Set<string>();
    const t = new Set<string>();
    if (!aktiv) return { belegeHell: b, buchungenHell: t };

    if (aktiv.art === 'beleg') {
      b.add(aktiv.id);
      for (const id of buchungenJeBeleg.get(aktiv.id) ?? []) t.add(id);
      // Eine Sammelüberweisung zieht die übrigen Belege mit: wer sie
      // anfasst, soll sehen, was sonst noch an ihr hängt.
      for (const id of t) for (const weiterer of belegeJeBuchung.get(id) ?? []) b.add(weiterer);
    } else {
      t.add(aktiv.id);
      for (const id of belegeJeBuchung.get(aktiv.id) ?? []) b.add(id);
      for (const id of b) for (const weitere of buchungenJeBeleg.get(id) ?? []) t.add(weitere);
    }
    return { belegeHell: b, buchungenHell: t };
  }, [aktiv, buchungenJeBeleg, belegeJeBuchung]);

  // Beide Spalten nach Datum, neueste zuerst — so stehen zusammengehörende
  // Zeilen meist auf gleicher Höhe, ohne dass irgendetwas erzwungen wird.
  const belegeSortiert = useMemo(
    () => [...belege].sort((a, b) => (b.rechnungsdatum ?? '').localeCompare(a.rechnungsdatum ?? '')),
    [belege],
  );
  const buchungenSortiert = useMemo(
    () => [...transaktionen].sort((a, b) => b.buchungsdatum.localeCompare(a.buchungsdatum)),
    [transaktionen],
  );

  const einengen = nurDiese && fixiert !== null;
  const belegeSichtbar = einengen
    ? belegeSortiert.filter((b) => belegeHell.has(b.id))
    : nurVerknuepfte
      ? belegeSortiert.filter((b) => (buchungenJeBeleg.get(b.id) ?? []).length > 0)
      : belegeSortiert;
  const buchungenSichtbar = einengen
    ? buchungenSortiert.filter((t) => buchungenHell.has(t.id))
    : nurVerknuepfte
      ? buchungenSortiert.filter((t) => (belegeJeBuchung.get(t.id) ?? []).length > 0)
      : buchungenSortiert;

  const verknuepfteBelege = belege.filter(
    (b) => (buchungenJeBeleg.get(b.id) ?? []).length > 0,
  ).length;

  /**
   * Beim Festhalten die Gegenspalte mitziehen.
   *
   * Eine Sammelüberweisung trifft Belege, die quer über das Jahr verteilt
   * sind — ohne das hier leuchtet etwas weit außerhalb des Sichtfelds, und
   * der Beirat weiß zwar, dass es drei sind, sieht aber nur einen. Gerollt
   * wird nur beim Klick, nicht beim Überfahren: eine Liste, die unter dem
   * Zeiger wegspringt, ist unbenutzbar.
   */
  useEffect(() => {
    if (!fixiert) return;
    const spalte = fixiert.art === 'beleg' ? buchungSpalte.current : belegSpalte.current;
    const hell = spalte?.querySelectorAll('.gegen-zeile.leuchtet');
    if (!spalte || !hell?.length) return;

    const spalteRaum = spalte.getBoundingClientRect();
    const erste = hell[0].getBoundingClientRect();
    const letzte = hell[hell.length - 1].getBoundingClientRect();

    // Passen alle Treffer gemeinsam in die Spalte, wird auf ihre Mitte
    // zentriert — dann sieht man die ganze Verbindung auf einen Blick.
    // Passen sie nicht, wäre die Mitte der Spanne der eine Ort, an dem
    // garantiert kein Treffer steht; dann zählt die erste Zeile.
    const spanne = letzte.bottom - erste.top;
    const bezug =
      spanne <= spalteRaum.height ? (erste.top + letzte.bottom) / 2 : erste.top + erste.height / 2;

    // Von Hand statt mit scrollIntoView: das würde auch das Fenster
    // mitrollen und dem Beirat die Kopfzeile unter dem Finger wegziehen,
    // während er nur in der anderen Spalte etwas sehen wollte.
    const versatz = bezug - spalteRaum.top - spalteRaum.height / 2;
    spalte.scrollBy({ top: versatz, behavior: 'smooth' });
  }, [fixiert]);

  function anfassen(art: 'beleg' | 'buchung', id: string) {
    return {
      onMouseEnter: () => setSchwebend({ art, id }),
      onMouseLeave: () => setSchwebend(null),
      onFocus: () => setSchwebend({ art, id }),
      onBlur: () => setSchwebend(null),
      onClick: () =>
        setFixiert((alt) => {
          const schonDa = alt?.art === art && alt.id === id;
          // Beim Lösen auch die Einengung aufheben, sonst bleibt der Beirat
          // vor zwei leeren Spalten stehen und sucht den Ausweg.
          if (schonDa) setNurDiese(false);
          return schonDa ? null : { art, id };
        }),
    };
  }

  if (!transaktionen.length) {
    return (
      <div className="leer card">
        <h3>Noch kein Kontoauszug eingespielt</h3>
        <p>
          Die Gegenüberstellung zeigt Belege und Buchungen nebeneinander. Dafür muss zuerst ein
          Kontoauszug eingespielt werden.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="gegen-leiste">
        <p className="gegen-hinweis">
          Zeile überfahren oder mit der Tastatur anspringen — die verknüpften Gegenstücke leuchten
          auf. Ein Klick hält die Verbindung fest.
          {fixiert && (
            <>
              <button
                className="btn ghost sm"
                aria-pressed={nurDiese}
                onClick={() => setNurDiese((a) => !a)}
              >
                {nurDiese ? 'Alle wieder zeigen' : 'Nur diese Verbindung'}
              </button>
              <button
                className="btn ghost sm"
                onClick={() => {
                  setFixiert(null);
                  setNurDiese(false);
                }}
              >
                Festhalten lösen
              </button>
            </>
          )}
        </p>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {vorschlaegeVorhanden && (
            <label className="gegen-schalter">
              <input
                type="checkbox"
                checked={mitVorschlaegen}
                onChange={(e) => setMitVorschlaegen(e.target.checked)}
              />
              Vorschläge mitzeigen
            </label>
          )}
          <label className="gegen-schalter">
            <input
              type="checkbox"
              checked={nurVerknuepfte}
              onChange={(e) => setNurVerknuepfte(e.target.checked)}
            />
            Nur Verknüpftes
          </label>
        </div>
      </div>

      <div className="gegen">
        {/* ── Belege ───────────────────────────────────────── */}
        <section className="gegen-spalte" aria-label="Belege">
          <header>
            <h3>Belege</h3>
            <span>
              {belegeSichtbar.length} · {verknuepfteBelege} mit Zahlung
            </span>
          </header>

          <div className="gegen-liste" ref={belegSpalte}>
            {belegeSichtbar.map((b) => {
              const befund = zahlungen[b.id];
              const anzahl = (buchungenJeBeleg.get(b.id) ?? []).length;
              return (
                <div
                  key={b.id}
                  role="button"
                  tabIndex={0}
                  className={`gegen-zeile${belegeHell.has(b.id) ? ' leuchtet' : ''}${
                    aktiv?.art === 'beleg' && aktiv.id === b.id ? ' quelle' : ''
                  }${anzahl === 0 ? ' ohne' : ''}${
                    vorschlagBelege.has(b.id) ? ' vorschlag' : ''
                  }`}
                  {...anfassen('beleg', b.id)}
                  onDoubleClick={() => onBeleg(b.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onBeleg(b.id);
                    }
                  }}
                  title="Enter oder Doppelklick öffnet den Beleg"
                >
                  <span className="d">{datumKurz(b.rechnungsdatum)}</span>
                  <span className="p">
                    <b>{b.lieferant ?? 'Ohne Lieferant'}</b>
                    <small className="mono">
                      {b.buchungszeilen[0]?.sachkontoNummer ?? '—'} · {b.rechnungsnummer ?? '—'}
                    </small>
                  </span>
                  <span className="a">{eur(b.brutto)}</span>
                  <span className="z">
                    <Haken zustand={b.pruefstatus} />
                    {vorschlagBelege.has(b.id) ? (
                      <span className="pill n" title="vorgeschlagene Zuordnung, noch nicht bestätigt">
                        ?
                      </span>
                    ) : anzahl > 1 ? (
                      <span className="pill n" title="in Raten oder gesammelt bezahlt">
                        {anzahl}×
                      </span>
                    ) : befund?.status === 'datum_weicht_ab' ? (
                      <span className="pill w" title="Zahldatum weicht vom Kontoauszug ab">
                        ≠
                      </span>
                    ) : anzahl === 0 ? (
                      <span className="pill n" title="keine Zahlung gefunden">
                        —
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })}
            {!belegeSichtbar.length && <p className="gegen-leer">Keine Belege in dieser Auswahl.</p>}
          </div>
        </section>

        {/* ── Buchungen ────────────────────────────────────── */}
        <section className="gegen-spalte" aria-label="Kontoauszug">
          <header>
            <h3>Kontoauszug</h3>
            <span>
              {buchungenSichtbar.length} · {ohneBeleg.size} ohne Beleg
            </span>
          </header>

          <div className="gegen-liste" ref={buchungSpalte}>
            {buchungenSichtbar.map((t) => {
              const anzahl = (belegeJeBuchung.get(t.id) ?? []).length;
              return (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  className={`gegen-zeile${buchungenHell.has(t.id) ? ' leuchtet' : ''}${
                    aktiv?.art === 'buchung' && aktiv.id === t.id ? ' quelle' : ''
                  }${ohneBeleg.has(t.id) ? ' ohne' : ''}${
                    vorschlagBuchungen.has(t.id) ? ' vorschlag' : ''
                  }`}
                  {...anfassen('buchung', t.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && ohneBeleg.has(t.id)) {
                      e.preventDefault();
                      onRueckfrage(t.id);
                    }
                  }}
                  title={
                    ohneBeleg.has(t.id)
                      ? 'Abbuchung ohne Beleg — Enter stellt eine Rückfrage'
                      : undefined
                  }
                >
                  <span className="d">{datumKurz(t.buchungsdatum)}</span>
                  <span className="p">
                    <b>{t.partnerName ?? 'Ohne Empfänger'}</b>
                    <small className="mono">{t.verwendungszweck ?? '—'}</small>
                  </span>
                  <span className={`a${t.richtung === 'eingang' ? ' pos' : ''}`}>
                    {t.richtung === 'eingang' ? '+' : '−'}
                    {eur(t.betrag)}
                  </span>
                  <span className="z">
                    <Haken zustand={transaktionStatus[t.id] ?? 'offen'} />
                    {anzahl > 1 ? (
                      <span
                        className="pill n"
                        title={
                          vorschlagBuchungen.has(t.id)
                            ? `Sammelzahlung über ${anzahl} Belege — vorgeschlagen, noch nicht bestätigt`
                            : 'Sammelzahlung'
                        }
                      >
                        {anzahl}×{vorschlagBuchungen.has(t.id) ? '?' : ''}
                      </span>
                    ) : ohneBeleg.has(t.id) ? (
                      <span className="pill w" title="ohne zugeordneten Beleg">
                        !
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })}
            {!buchungenSichtbar.length && (
              <p className="gegen-leer">Keine Buchungen in dieser Auswahl.</p>
            )}
          </div>
        </section>
      </div>

      {aktiv && (
        <Verbindung
          aktiv={aktiv}
          {...{ belegeHell, buchungenHell, belege, transaktionNach, zahlungen }}
        />
      )}

      {/* Die Bedienung hängt an der festgehaltenen Verbindung, nicht am
          Überfahren: ein Knopf, der verschwindet, sobald man auf ihn
          zusteuert, ist keiner. */}
      {fixiert && (
        <Werkbank
          fixiert={fixiert}
          belegeHell={belegeHell}
          buchungenHell={buchungenHell}
          belege={belege}
          transaktionNach={transaktionNach}
          zuordnungen={zuordnungen}
          transaktionStatus={transaktionStatus}
          onBeleg={onBeleg}
          onRueckfrage={onRueckfrage}
          onTransaktionStatus={onTransaktionStatus}
          onVerknuepfen={onVerknuepfen}
          onZuordnung={onZuordnung}
        />
      )}
    </>
  );
}

/**
 * Was man mit der festgehaltenen Verbindung tun kann.
 *
 * Alles an einem Ort statt verteilt über beide Spalten: Wer eine Verbindung
 * festhält, will etwas damit machen — bestätigen, lösen, oder einen Beleg
 * dazunehmen, den der Abgleich nicht gefunden hat.
 */
function Werkbank({
  fixiert,
  belegeHell,
  buchungenHell,
  belege,
  transaktionNach,
  zuordnungen,
  transaktionStatus,
  onBeleg,
  onRueckfrage,
  onTransaktionStatus,
  onVerknuepfen,
  onZuordnung,
}: {
  fixiert: NonNullable<Auswahl>;
  belegeHell: Set<string>;
  buchungenHell: Set<string>;
  belege: Beleg[];
  transaktionNach: Map<string, Banktransaktion>;
  zuordnungen: Zuordnung[];
  transaktionStatus: Record<string, PruefStatus>;
  onBeleg: (belegId: string) => void;
  onRueckfrage: (transaktionId: string) => void;
  onTransaktionStatus: (transaktionId: string, status: PruefStatus) => void;
  onVerknuepfen: (transaktionId: string, belegId: string) => void;
  onZuordnung: (zuordnungId: string, was: 'bestaetigen' | 'loesen') => void;
}) {
  const [suche, setSuche] = useState('');
  const [sucheOffen, setSucheOffen] = useState(false);

  // Die Position, um die es geht. Bei einem festgehaltenen Beleg ist das
  // seine erste Zahlung — gibt es keine, kann man hier nur suchen.
  const transaktionId =
    fixiert.art === 'buchung' ? fixiert.id : [...buchungenHell][0] ?? null;
  const transaktion = transaktionId ? transaktionNach.get(transaktionId) : null;

  const betroffene = zuordnungen.filter(
    (z) =>
      (buchungenHell.has(z.transaktionId) || belegeHell.has(z.belegId)) &&
      z.status !== 'nicht_belegpflichtig',
  );
  const vorschlaege = betroffene.filter((z) => z.status === 'vorschlag');
  const status = transaktionId ? (transaktionStatus[transaktionId] ?? 'offen') : 'offen';

  // Belege, die noch nicht an dieser Position hängen — nur die lassen sich
  // sinnvoll dazunehmen.
  const treffer = suche.trim()
    ? belege
        .filter((b) => !belegeHell.has(b.id))
        .filter((b) => {
          const q = suche.trim().toLowerCase();
          return (
            (b.lieferant ?? '').toLowerCase().includes(q) ||
            (b.rechnungsnummer ?? '').toLowerCase().includes(q) ||
            String(b.brutto ?? '').includes(q)
          );
        })
        .slice(0, 8)
    : [];

  return (
    <div className="werkbank">
      <div className="werkbank-reihe">
        {transaktion && (
          <>
            <button
              className={`btn sm${status === 'geprueft' ? '' : ' ok'}`}
              onClick={() =>
                onTransaktionStatus(transaktion.id, status === 'geprueft' ? 'offen' : 'geprueft')
              }
            >
              {status === 'geprueft' ? 'Bestätigung zurücknehmen' : 'Position bestätigen'}
            </button>
            <button className="btn warnline sm" onClick={() => onRueckfrage(transaktion.id)}>
              Rückfrage
            </button>
          </>
        )}

        {vorschlaege.length > 0 && (
          <button
            className="btn primary sm"
            onClick={() => vorschlaege.forEach((z) => onZuordnung(z.id, 'bestaetigen'))}
          >
            {vorschlaege.length === 1
              ? 'Vorschlag bestätigen'
              : `${vorschlaege.length} Vorschläge bestätigen`}
          </button>
        )}

        {transaktion && (
          <button className="btn ghost sm" onClick={() => setSucheOffen((o) => !o)}>
            {sucheOffen ? 'Suche schließen' : 'Beleg dazunehmen'}
          </button>
        )}
      </div>

      {sucheOffen && transaktion && (
        <div className="werkbank-suche">
          <input
            type="search"
            autoFocus
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Lieferant, Rechnungsnummer oder Betrag …"
            aria-label="Beleg suchen"
          />
          {treffer.map((b) => (
            <button
              key={b.id}
              className="werkbank-treffer"
              onClick={() => {
                onVerknuepfen(transaktion.id, b.id);
                setSuche('');
                setSucheOffen(false);
              }}
            >
              <span>
                <b>{b.lieferant ?? 'Ohne Lieferant'}</b>
                <small className="mono">
                  {datumKurz(b.rechnungsdatum)} · {b.rechnungsnummer ?? '—'}
                </small>
              </span>
              <span className="mono">{eur(b.brutto)}</span>
            </button>
          ))}
          {suche.trim() && !treffer.length && (
            <p className="gegen-leer" style={{ padding: 14 }}>
              Kein Beleg passt zu „{suche.trim()}".
            </p>
          )}
        </div>
      )}

      {betroffene.length > 0 && (
        <ul className="werkbank-liste">
          {betroffene.map((z) => {
            const beleg = belege.find((b) => b.id === z.belegId);
            return (
              <li key={z.id}>
                <button className="werkbank-beleg" onClick={() => beleg && onBeleg(beleg.id)}>
                  {beleg?.lieferant ?? 'Beleg unbekannt'}
                </button>
                <span className="mono">{eur(z.betrag)}</span>
                {z.status === 'vorschlag' && <span className="pill n">Vorschlag</span>}
                {z.quelle === 'manuell' && <span className="pill n">von Hand</span>}
                <button
                  className="btn ghost sm"
                  onClick={() => onZuordnung(z.id, 'loesen')}
                  title="Diese Verknüpfung lösen"
                >
                  Lösen
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Was die gerade betrachtete Verbindung bedeutet, in einem Satz.
 *
 * Die Hervorhebung zeigt, dass etwas zusammengehört. Sie zeigt nicht, ob es
 * stimmt — dafür steht hier die Zahl: Betrag und Tage zwischen den beiden
 * Zahldaten.
 */
function Verbindung({
  aktiv,
  belegeHell,
  buchungenHell,
  belege,
  transaktionNach,
  zahlungen,
}: {
  aktiv: NonNullable<Auswahl>;
  belegeHell: Set<string>;
  buchungenHell: Set<string>;
  belege: Beleg[];
  transaktionNach: Map<string, Banktransaktion>;
  zahlungen: Record<string, Zahlungsbefund>;
}) {
  const dazu = belege.filter((b) => belegeHell.has(b.id));
  const buchungen = [...buchungenHell].map((id) => transaktionNach.get(id)).filter(Boolean);

  if (!dazu.length && !buchungen.length) return null;

  const summeBelege = dazu.reduce((s, b) => s + (b.brutto ?? 0), 0);
  const summeBuchungen = buchungen.reduce((s, t) => s + (t!.betrag ?? 0), 0);
  const differenz = Math.round((summeBelege - summeBuchungen) * 100) / 100;

  // Nur eine Seite getroffen: dann gibt es nichts zu vergleichen.
  if (!dazu.length || !buchungen.length) {
    return (
      <div className="gegen-fuss">
        {aktiv.art === 'beleg'
          ? 'Zu diesem Beleg wurde im Kontoauszug keine Buchung gefunden.'
          : 'Zu dieser Buchung liegt kein Beleg vor.'}
      </div>
    );
  }

  const befund = dazu.length === 1 ? zahlungen[dazu[0].id] : null;

  return (
    <div className="gegen-fuss">
      <span>
        {dazu.length} {dazu.length === 1 ? 'Beleg' : 'Belege'} · {eur(summeBelege)}
      </span>
      <span aria-hidden>↔</span>
      <span>
        {buchungen.length} {buchungen.length === 1 ? 'Buchung' : 'Buchungen'} ·{' '}
        {eur(summeBuchungen)}
      </span>
      {Math.abs(differenz) > 0.005 && (
        <span className="warn">Differenz {eur(differenz)}</span>
      )}
      {befund?.abweichungTage !== null && befund?.abweichungTage !== undefined && befund.abweichungTage !== 0 && (
        <span className={befund.status === 'datum_weicht_ab' ? 'warn' : undefined}>
          Zahldatum {datum(befund.zahldatumLautBuchhaltung)} laut Buchhaltung,{' '}
          {datum(befund.zahldatumLautKontoauszug)} laut Kontoauszug —{' '}
          {Math.abs(befund.abweichungTage)} Tage
        </span>
      )}
    </div>
  );
}
